/*!
 * Baby In — STEP 10.1B.3
 * Google Ads click-identifier capture with DUAL-PATH attribution.
 *
 *   Google Ads click
 *        |
 *   persistent first-party storage (90 days, consent-gated)
 *        |
 *        +-- standard cart path ..... cart attributes -> Order.customAttributes
 *        |     google_click_id / google_click_id_type / google_click_timestamp
 *        |
 *        +-- Buy Now / accelerated checkout path ..... line item properties -> Order line item
 *              properties[_google_click_id] / [_google_click_id_type] / [_google_click_timestamp]
 *
 * Cart writes are verified and retried (hard cap: 3 writes per page view).
 * Consent withdrawal, 90-day expiry and invalid records clear EVERY surface:
 * cookie, localStorage, cart attributes and product-form properties.
 *
 * Never modifies cart items, quantities, note, discounts or totals.
 * Never sends a conversion. Never touches GA4 / Google Ads tags.
 */
(function () {
  'use strict';

  var VERSION = '10.1B.3';

  var PARAM_NAMES = ['gclid', 'gbraid', 'wbraid'];

  var STORE_KEYS = {
    id: 'bi_google_click_id',
    type: 'bi_google_click_type',
    ts: 'bi_google_click_ts'
  };

  var CART_ATTRS = {
    id: 'google_click_id',
    type: 'google_click_id_type',
    ts: 'google_click_timestamp'
  };

  /* underscore prefix = hidden from the customer, still visible on the order */
  var PROP_KEYS = {
    id: '_google_click_id',
    type: '_google_click_id_type',
    ts: '_google_click_timestamp'
  };

  var MAX_AGE_DAYS = 90;
  var MAX_AGE_MS = MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
  var CLOCK_SKEW_TOLERANCE_MS = 24 * 60 * 60 * 1000;

  var MAX_CART_WRITES = 3;
  var VERIFY_DELAY_MS = 150;
  var RETRY_DELAYS_MS = [250, 750];
  var LATE_VERIFY_MS = 3000;

  var nativeFetch = (typeof window.fetch === 'function') ? window.fetch.bind(window) : null;

  var state = {
    syncing: false,
    lastPushed: null,
    lastResult: null,
    anomaly: null,
    consent: null,
    cartUpdateCalls: 0,
    clearingCart: false,
    lastClear: null,
    lastWriteAttempts: 0,
    attributionWriteFailed: false,
    formsPatched: 0,
    lateVerifyScheduled: false
  };

  function delay(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  /* ------------------------------------------------------------------ */
  /* first-party cookie helpers                                          */
  /* ------------------------------------------------------------------ */

  function rootDomain() {
    var host = window.location.hostname || '';
    var parts = host.split('.');
    if (parts.length < 3) return host;
    return parts.slice(-2).join('.');
  }

  function setCookie(name, value, maxAgeSeconds) {
    if (maxAgeSeconds <= 0) return;
    try {
      var secure = (window.location.protocol === 'https:') ? '; Secure' : '';
      document.cookie = name + '=' + encodeURIComponent(value) +
        '; path=/; max-age=' + Math.floor(maxAgeSeconds) +
        '; domain=.' + rootDomain() +
        '; SameSite=Lax' + secure;
    } catch (e) { /* no-op */ }
  }

  function getCookie(name) {
    try {
      var parts = (document.cookie || '').split(';');
      for (var i = 0; i < parts.length; i++) {
        var p = parts[i];
        var eq = p.indexOf('=');
        if (eq === -1) continue;
        if (p.slice(0, eq).trim() !== name) continue;
        return decodeURIComponent(p.slice(eq + 1));
      }
    } catch (e) { /* no-op */ }
    return null;
  }

  function deleteCookie(name) {
    try {
      document.cookie = name + '=; path=/; max-age=0; domain=.' + rootDomain();
      document.cookie = name + '=; path=/; max-age=0';
    } catch (e) { /* no-op */ }
  }

  /* ------------------------------------------------------------------ */
  /* consent                                                             */
  /* ------------------------------------------------------------------ */

  function privacyApi() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      return (cp && typeof cp.marketingAllowed === 'function') ? cp : null;
    } catch (e) { return null; }
  }

  function marketingAllowed() {
    var api = privacyApi();
    if (!api) { state.consent = 'api-unavailable'; return false; }
    try {
      var allowed = api.marketingAllowed() === true;
      state.consent = allowed ? 'allowed' : 'denied';
      return allowed;
    } catch (e) {
      state.consent = 'api-error';
      return false;
    }
  }

  function whenPrivacyApiReady(cb) {
    if (privacyApi()) { cb(); return; }
    try {
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          function () { cb(); }
        );
        return;
      }
    } catch (e) { /* fall through */ }
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      if (privacyApi() || tries > 40) { clearInterval(timer); cb(); }
    }, 250);
  }

  /* ------------------------------------------------------------------ */
  /* storage                                                             */
  /* ------------------------------------------------------------------ */

  function isValidIdentifier(value) {
    if (!value) return false;
    if (value.length < 4 || value.length > 512) return false;
    return !/[\s"'<>;,\\]/.test(value);
  }

  function writeStored(rec) {
    var seconds = Math.floor(MAX_AGE_MS / 1000);
    setCookie(STORE_KEYS.id, rec.id, seconds);
    setCookie(STORE_KEYS.type, rec.type, seconds);
    setCookie(STORE_KEYS.ts, rec.ts, seconds);
    try {
      localStorage.setItem(STORE_KEYS.id, rec.id);
      localStorage.setItem(STORE_KEYS.type, rec.type);
      localStorage.setItem(STORE_KEYS.ts, rec.ts);
    } catch (e) { /* no-op */ }
    state.lastPushed = null;
  }

  function clearStored() {
    deleteCookie(STORE_KEYS.id);
    deleteCookie(STORE_KEYS.type);
    deleteCookie(STORE_KEYS.ts);
    try {
      localStorage.removeItem(STORE_KEYS.id);
      localStorage.removeItem(STORE_KEYS.type);
      localStorage.removeItem(STORE_KEYS.ts);
    } catch (e) { /* no-op */ }
    state.lastPushed = null;
  }

  function readStored() {
    var id = getCookie(STORE_KEYS.id);
    var type = getCookie(STORE_KEYS.type);
    var ts = getCookie(STORE_KEYS.ts);
    var fromCookie = !!(id && type && ts);

    if (!fromCookie) {
      try {
        id = localStorage.getItem(STORE_KEYS.id);
        type = localStorage.getItem(STORE_KEYS.type);
        ts = localStorage.getItem(STORE_KEYS.ts);
      } catch (e) { /* no-op */ }
    }

    if (!id || !type || !ts) return null;
    if (PARAM_NAMES.indexOf(type) === -1) { purgeAll('invalid-type'); return null; }
    if (!isValidIdentifier(id)) { purgeAll('invalid-identifier'); return null; }

    var clickedAt = Date.parse(ts);
    if (!isFinite(clickedAt)) { purgeAll('invalid-timestamp'); return null; }

    var age = Date.now() - clickedAt;
    if (age > MAX_AGE_MS) { purgeAll('expired-90-days'); return null; }
    if (age < -CLOCK_SKEW_TOLERANCE_MS) { purgeAll('timestamp-in-future'); return null; }

    /* re-arm so the 90 days always run from the click, not from this visit */
    var remaining = Math.floor((MAX_AGE_MS - age) / 1000);
    if (remaining > 0) {
      setCookie(STORE_KEYS.id, id, remaining);
      setCookie(STORE_KEYS.type, type, remaining);
      setCookie(STORE_KEYS.ts, ts, remaining);
    }

    return { id: id, type: type, ts: ts, ageMs: age, source: fromCookie ? 'cookie' : 'localStorage' };
  }

  /* ------------------------------------------------------------------ */
  /* capture                                                             */
  /* ------------------------------------------------------------------ */

  function identifiersInUrl() {
    var found = [];
    try {
      var qs = new URLSearchParams(window.location.search);
      for (var i = 0; i < PARAM_NAMES.length; i++) {
        var raw = qs.get(PARAM_NAMES[i]);
        if (raw === null) continue;
        var value = String(raw).trim();
        if (isValidIdentifier(value)) found.push({ type: PARAM_NAMES[i], id: value });
      }
    } catch (e) { /* no-op */ }
    return found;
  }

  function capture() {
    var found = identifiersInUrl();

    if (found.length === 0) {
      return { action: 'no-identifier-in-url' };
    }

    if (found.length > 1) {
      /* More than one Google identifier on the same URL. There is no
         documented, provable precedence rule, so we refuse to invent one:
         we record the anomaly and leave any existing stored click intact. */
      state.anomaly = {
        at: new Date().toISOString(),
        types: found.map(function (f) { return f.type; })
      };
      try {
        sessionStorage.setItem('bi_google_click_anomaly', JSON.stringify(state.anomaly));
      } catch (e) { /* no-op */ }
      return { action: 'anomaly-multiple-identifiers', types: state.anomaly.types };
    }

    var rec = { id: found[0].id, type: found[0].type, ts: new Date().toISOString() };
    writeStored(rec);
    return { action: 'captured', type: rec.type };
  }

  /* ------------------------------------------------------------------ */
  /* PATH B — product form line item properties (Buy Now fallback)       */
  /* ------------------------------------------------------------------ */

  var FORM_SELECTOR = 'form[action*="/cart/add"]';
  var ACCELERATED_SELECTOR = '.shopify-payment-button, shopify-accelerated-checkout, shopify-buy-it-now-button';

  function formNeedsProps(form) {
    try { return !!form.querySelector(ACCELERATED_SELECTOR); } catch (e) { return false; }
  }

  function propInput(form, key) {
    try { return form.querySelector('input[data-bi-google-prop="' + key + '"]'); } catch (e) { return null; }
  }

  function setProp(form, key, value) {
    var input = propInput(form, key);
    if (value === null) {
      if (input && input.parentNode) input.parentNode.removeChild(input);
      return;
    }
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.setAttribute('data-bi-google-prop', key);
      input.name = 'properties[' + key + ']';
      form.appendChild(input);
    }
    if (input.value !== value) input.value = value;
  }

  function syncProductForms() {
    var rec = null;
    if (marketingAllowed()) rec = readStored();
    var forms;
    try { forms = document.querySelectorAll(FORM_SELECTOR); } catch (e) { return 0; }
    var patched = 0;
    for (var i = 0; i < forms.length; i++) {
      var form = forms[i];
      var wanted = rec && formNeedsProps(form);
      if (wanted) {
        setProp(form, PROP_KEYS.id, rec.id);
        setProp(form, PROP_KEYS.type, rec.type);
        setProp(form, PROP_KEYS.ts, rec.ts);
        patched++;
      } else {
        setProp(form, PROP_KEYS.id, null);
        setProp(form, PROP_KEYS.type, null);
        setProp(form, PROP_KEYS.ts, null);
      }
    }
    state.formsPatched = patched;
    return patched;
  }

  function clearProductForms() {
    var forms;
    try { forms = document.querySelectorAll(FORM_SELECTOR); } catch (e) { return; }
    for (var i = 0; i < forms.length; i++) {
      setProp(forms[i], PROP_KEYS.id, null);
      setProp(forms[i], PROP_KEYS.type, null);
      setProp(forms[i], PROP_KEYS.ts, null);
    }
    state.formsPatched = 0;
  }

  var formTimer = null;
  function scheduleFormSync() {
    if (formTimer) return;
    formTimer = setTimeout(function () { formTimer = null; syncProductForms(); }, 120);
  }

  function installFormObserver() {
    try {
      var observer = new MutationObserver(function () { scheduleFormSync(); });
      (function start() {
        if (document.body) {
          observer.observe(document.body, { childList: true, subtree: true });
          syncProductForms();
        } else {
          setTimeout(start, 50);
        }
      })();
    } catch (e) { /* no-op */ }
  }

  /* ------------------------------------------------------------------ */
  /* PATH A — cart attributes, with verify + bounded retry               */
  /* ------------------------------------------------------------------ */

  function isCartMutation(url) {
    try {
      return /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/.test(String(url));
    } catch (e) { return false; }
  }

  function getCart() {
    return nativeFetch('/cart.js', {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    }).then(function (r) { return r.json(); });
  }

  function cartMatchesRecord(cart, rec) {
    var a = (cart && cart.attributes) || {};
    return a[CART_ATTRS.id] === rec.id &&
           a[CART_ATTRS.type] === rec.type &&
           a[CART_ATTRS.ts] === rec.ts;
  }

  function postAttributes(values) {
    var body = { attributes: {} };
    body.attributes[CART_ATTRS.id] = values.id;
    body.attributes[CART_ATTRS.type] = values.type;
    body.attributes[CART_ATTRS.ts] = values.ts;
    state.cartUpdateCalls++;
    return nativeFetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  function signatureOf(rec) { return rec.type + '|' + rec.id + '|' + rec.ts; }

  /* write -> verify -> retry, hard-capped at MAX_CART_WRITES per page view */
  function writeAndVerify(rec, attempt) {
    return postAttributes(rec)
      .then(function () { return delay(VERIFY_DELAY_MS); })
      .then(getCart)
      .then(function (cart) {
        if (cartMatchesRecord(cart, rec)) {
          state.lastPushed = signatureOf(rec);
          state.lastWriteAttempts = attempt + 1;
          scheduleLateVerify(rec);
          return { updated: true, writes: attempt + 1, verified: true };
        }
        if (state.cartUpdateCalls >= MAX_CART_WRITES || attempt + 1 >= RETRY_DELAYS_MS.length + 1) {
          state.attributionWriteFailed = true;
          state.lastWriteAttempts = attempt + 1;
          try { sessionStorage.setItem('bi_attribution_write_failed', new Date().toISOString()); } catch (e) { /* no-op */ }
          return { attribution_write_failed: true, writes: attempt + 1, verified: false };
        }
        return delay(RETRY_DELAYS_MS[attempt]).then(function () {
          return writeAndVerify(rec, attempt + 1);
        });
      });
  }

  /* one late re-check: catches another cart writer overwriting us seconds later */
  function scheduleLateVerify(rec) {
    if (state.lateVerifyScheduled) return;
    state.lateVerifyScheduled = true;
    setTimeout(function () {
      if (!marketingAllowed()) return;
      var current = readStored();
      if (!current || signatureOf(current) !== signatureOf(rec)) return;
      if (state.cartUpdateCalls >= MAX_CART_WRITES) return;
      getCart().then(function (cart) {
        if (cartMatchesRecord(cart, rec)) return;
        if (state.cartUpdateCalls >= MAX_CART_WRITES) return;
        state.syncing = true;
        return postAttributes(rec)
          .then(function () { return delay(VERIFY_DELAY_MS); })
          .then(getCart)
          .then(function (c2) {
            state.syncing = false;
            if (cartMatchesRecord(c2, rec)) {
              state.lastResult = { updated: true, lateGuard: true, writes: state.cartUpdateCalls };
            } else {
              state.attributionWriteFailed = true;
              try { sessionStorage.setItem('bi_attribution_write_failed', new Date().toISOString()); } catch (e) { /* no-op */ }
              state.lastResult = { attribution_write_failed: true, lateGuard: true, writes: state.cartUpdateCalls };
            }
          });
      }).catch(function () { state.syncing = false; });
    }, LATE_VERIFY_MS);
  }

  function syncCart(force) {
    if (!nativeFetch) {
      return Promise.resolve({ skipped: 'fetch-unavailable' });
    }
    if (!marketingAllowed()) {
      return Promise.resolve({ skipped: 'consent-not-granted' });
    }
    var rec = readStored();
    if (!rec) {
      /* No stored Google click: never write empty attributes, never clear. */
      return Promise.resolve({ skipped: 'no-stored-click' });
    }
    var signature = signatureOf(rec);
    if (!force && state.lastPushed === signature) {
      return Promise.resolve({ skipped: 'already-synced' });
    }
    if (state.syncing) {
      return Promise.resolve({ skipped: 'sync-in-flight' });
    }
    if (state.cartUpdateCalls >= MAX_CART_WRITES) {
      return Promise.resolve({ skipped: 'write-cap-reached', writes: state.cartUpdateCalls });
    }

    state.syncing = true;
    return getCart()
      .then(function (cart) {
        if (cartMatchesRecord(cart, rec)) {
          state.lastPushed = signature;
          return { skipped: 'cart-already-current' };
        }
        return writeAndVerify(rec, 0);
      })
      .catch(function (e) {
        return { error: String((e && e.message) || e) };
      })
      .then(function (result) {
        state.syncing = false;
        state.lastResult = result;
        syncProductForms();
        return result;
      });
  }

  /* clears ONLY the three Google attributes; merges, so other keys survive */
  function clearCartAttribution(reason) {
    if (!nativeFetch) return Promise.resolve({ skipped: 'fetch-unavailable' });
    if (state.clearingCart) return Promise.resolve({ skipped: 'clear-in-flight' });
    state.clearingCart = true;
    return getCart()
      .then(function (cart) {
        var a = (cart && cart.attributes) || {};
        var keys = [CART_ATTRS.id, CART_ATTRS.type, CART_ATTRS.ts];
        var present = keys.filter(function (k) {
          return Object.prototype.hasOwnProperty.call(a, k) && a[k] !== '' && a[k] != null;
        });
        if (present.length === 0) {
          return { skipped: 'nothing-to-clear', reason: reason || null };
        }
        state.syncing = true;
        return postAttributes({ id: '', type: '', ts: '' })
          .then(function () { return delay(VERIFY_DELAY_MS); })
          .then(getCart)
          .then(function (c2) {
            state.syncing = false;
            var b = (c2 && c2.attributes) || {};
            var still = keys.filter(function (k) {
              return Object.prototype.hasOwnProperty.call(b, k) && b[k] !== '' && b[k] != null;
            });
            return { cleared: present, remaining: still, reason: reason || null };
          });
      })
      .catch(function (e) {
        state.syncing = false;
        return { error: String((e && e.message) || e), reason: reason || null };
      })
      .then(function (result) {
        state.clearingCart = false;
        state.lastPushed = null;
        state.lastClear = result;
        return result;
      });
  }

  /* every attribution surface dies together */
  function purgeAll(reason) {
    clearStored();
    clearProductForms();
    return clearCartAttribution(reason);
  }

  var scheduled = null;
  function scheduleSync() {
    if (scheduled) clearTimeout(scheduled);
    scheduled = setTimeout(function () { scheduled = null; syncCart(false); }, 400);
  }

  /* ------------------------------------------------------------------ */
  /* cart lifecycle hooks (add to cart / drawer / cart page / quantity)   */
  /* ------------------------------------------------------------------ */

  function installFetchHook() {
    if (typeof window.fetch !== 'function') return;
    var original = window.fetch;
    window.fetch = function (input, init) {
      var url = '';
      try { url = (typeof input === 'string') ? input : ((input && input.url) || ''); } catch (e) { /* no-op */ }
      var promise = original.apply(this, arguments);
      try {
        if (url && isCartMutation(url) && !state.syncing) {
          promise.then(function () { scheduleSync(); }, function () { /* no-op */ });
        }
      } catch (e) { /* no-op */ }
      return promise;
    };
  }

  function installXhrHook() {
    var proto = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
    if (!proto || typeof proto.open !== 'function') return;
    var open = proto.open;
    var send = proto.send;
    proto.open = function (method, url) {
      try { this.__biCartUrl = url; } catch (e) { /* no-op */ }
      return open.apply(this, arguments);
    };
    proto.send = function () {
      try {
        var self = this;
        if (self.__biCartUrl && isCartMutation(self.__biCartUrl)) {
          self.addEventListener('loadend', function () {
            if (!state.syncing) scheduleSync();
          });
        }
      } catch (e) { /* no-op */ }
      return send.apply(this, arguments);
    };
  }

  /* ------------------------------------------------------------------ */
  /* boot                                                                */
  /* ------------------------------------------------------------------ */

  function run() {
    if (!marketingAllowed()) {
      /* No marketing consent: create nothing, and remove every trace that
         already exists - storage, cart attributes and form properties. */
      state.lastResult = { skipped: 'consent-not-granted' };
      purgeAll('consent-not-granted');
      return;
    }
    state.lastResult = capture();
    syncProductForms();
    syncCart(false);
  }

  function boot() {
    installFetchHook();
    installXhrHook();
    installFormObserver();

    whenPrivacyApiReady(run);

    document.addEventListener('visitorConsentCollected', function () {
      run();
    });

    window.addEventListener('pageshow', function (e) {
      if (e && e.persisted) { scheduleSync(); scheduleFormSync(); }
    });
  }

  /* ------------------------------------------------------------------ */
  /* diagnostics (read-only; used by STEP 10.1B synthetic QA)            */
  /* ------------------------------------------------------------------ */

  function formPropReport() {
    var forms;
    try { forms = document.querySelectorAll(FORM_SELECTOR); } catch (e) { return []; }
    return [].map.call(forms, function (form, i) {
      return {
        index: i,
        hasAcceleratedCheckout: formNeedsProps(form),
        props: [PROP_KEYS.id, PROP_KEYS.type, PROP_KEYS.ts].map(function (k) {
          var input = propInput(form, k);
          return { key: k, present: !!input, name: input ? input.name : null, hasValue: !!(input && input.value) };
        })
      };
    });
  }

  window.BIGoogleClick = {
    version: VERSION,
    keys: STORE_KEYS,
    cartAttributeNames: CART_ATTRS,
    lineItemPropertyNames: PROP_KEYS,
    maxAgeDays: MAX_AGE_DAYS,
    maxCartWrites: MAX_CART_WRITES,
    consent: function () { return state.consent; },
    marketingAllowed: marketingAllowed,
    status: function () {
      var rec = readStored();
      return {
        version: VERSION,
        consent: state.consent,
        hasStoredClick: !!rec,
        storedType: rec ? rec.type : null,
        storedTimestamp: rec ? rec.ts : null,
        storedAgeMs: rec ? rec.ageMs : null,
        storedSource: rec ? rec.source : null,
        lastAction: state.lastResult,
        lastClear: state.lastClear,
        cartUpdateCalls: state.cartUpdateCalls,
        lastWriteAttempts: state.lastWriteAttempts,
        attributionWriteFailed: state.attributionWriteFailed,
        formsPatched: state.formsPatched,
        anomaly: state.anomaly
      };
    },
    /* returns true/false only — never echoes the stored identifier */
    matches: function (expected) {
      var rec = readStored();
      return !!rec && rec.id === expected;
    },
    formProps: formPropReport,
    formPropsMatch: function (expected) {
      var forms;
      try { forms = document.querySelectorAll(FORM_SELECTOR); } catch (e) { return false; }
      var checked = 0;
      for (var i = 0; i < forms.length; i++) {
        if (!formNeedsProps(forms[i])) continue;
        var a = propInput(forms[i], PROP_KEYS.id);
        if (!a || a.value !== expected) return false;
        checked++;
      }
      return checked > 0;
    },
    cartMatches: function () {
      if (!nativeFetch) return Promise.resolve(null);
      var rec = readStored();
      return getCart().then(function (cart) {
        var a = (cart && cart.attributes) || {};
        return {
          cartHasAttributes: Object.prototype.hasOwnProperty.call(a, CART_ATTRS.id),
          cartAttributeKeys: Object.keys(a),
          matchesStored: !!rec && cartMatchesRecord(cart, rec),
          cartItemCount: cart ? cart.item_count : null,
          cartTotal: cart ? cart.total_price : null,
          cartNoteIsNull: !cart || cart.note === null || cart.note === ''
        };
      });
    },
    syncNow: function () { return syncCart(true); },
    syncFormsNow: syncProductForms,
    reevaluate: function () { run(); return state.lastResult; },
    clearCartAttribution: clearCartAttribution,
    /* QA cleanup helper: removes stored click, form props AND cart attributes */
    purge: function () {
      clearStored();
      clearProductForms();
      try {
        sessionStorage.removeItem('bi_google_click_anomaly');
        sessionStorage.removeItem('bi_attribution_write_failed');
      } catch (e) { /* no-op */ }
      state.anomaly = null;
      state.attributionWriteFailed = false;
      return clearCartAttribution('qa-purge').then(function (r) {
        return { purged: true, cart: r, formsCleared: true };
      });
    },
    /* QA only: resets the per-page-view write cap so a test run can continue */
    resetWriteBudget: function () {
      state.cartUpdateCalls = 0;
      state.lateVerifyScheduled = false;
      state.lastPushed = null;
      state.attributionWriteFailed = false;
      return true;
    }
  };

  boot();
})();
